import { Injectable, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, map, switchMap, of, tap } from 'rxjs';
import { GoogleAnalyticsService } from './google-analytics.service';
import { PosthogService } from './posthog.service';
import * as Sentry from "@sentry/angular";

interface DecodedToken {
  permissions?: string[];
  'tevuna/roles'?: string[];
  roles?: string[];
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class MyAuthService {
  private auth = inject(AuthService);
  private gaService = inject(GoogleAnalyticsService);
  private posthogService = inject(PosthogService);

  constructor() {
    this.setupUserTracking();
  }

  private setupUserTracking() {
    this.auth.user$.pipe(
      switchMap(user => {
        if (user && user.sub) {
          // Get user roles and combine with user data for comprehensive tracking
          return this.getRoles().pipe(
            tap(roles => {
              // Google Analytics tracking
              if (user.sub) {
                this.gaService.setUserId(user.sub);
              }
              this.gaService.setUserProperties(user.email || '');

              // Sentry user tracking
              Sentry.setUser({
                id: user.sub,
                email: user.email,
                username: user.name
              });

              // PostHog user identification with roles
              if (user.sub) {
                this.posthogService.identify(user.sub, {
                  email: user.email || '',
                  name: user.name || '',
                  nickname: user.nickname || '',
                  picture: user.picture || '',
                  email_verified: user.email_verified || false,
                  updated_at: user.updated_at || '',
                  roles: roles,
                  is_admin: roles.includes('admin'),
                  is_sales: roles.includes('sales')
                });

                // Track login event with role information
                this.posthogService.capture('user_logged_in', {
                  method: 'auth0',
                  email: user.email || '',
                  name: user.name || '',
                  roles: roles,
                  is_admin: roles.includes('admin'),
                  is_sales: roles.includes('sales')
                });
              }
            })
          );
        } else {
          // Clear tracking when not authenticated
          Sentry.setUser(null);
          this.posthogService.reset();
          return of(null);
        }
      })
    ).subscribe();
  }

  getPermissions(): Observable<string[]> {
    return this.auth.getAccessTokenSilently().pipe(
      switchMap(token => {
        const decodedToken = this.decodeToken(token);
        return of(decodedToken?.permissions || []);
      })
    );
  }

  getRoles(): Observable<string[]> {
    return this.auth.getAccessTokenSilently().pipe(
      switchMap(token => {
        const decodedToken = this.decodeToken(token);
        // Check for namespaced roles first, then fallback to regular roles property
        return of(decodedToken?.['tevuna/roles'] || decodedToken?.roles || []);
      })
    );
  }

  hasRole(role: string): Observable<boolean> {
    return this.getRoles().pipe(
      map(roles => roles.includes(role))
    );
  }

  hasAnyRole(roles: string[]): Observable<boolean> {
    return this.getRoles().pipe(
      map(userRoles => roles.some(role => userRoles.includes(role)))
    );
  }

  hasPermission(permission: string): Observable<boolean> {
    return this.getPermissions().pipe(map(permissions => permissions.includes(permission)));
  }

  hasAnyPermission(permissions: string[]): Observable<boolean> {
    return this.getPermissions().pipe(map(userPermissions => permissions.some(permission => userPermissions.includes(permission))));
  }

  isAdmin(): Observable<boolean> {
    return this.hasRole('admin').pipe(switchMap(isRoleAdmin => isRoleAdmin ? of(true) : this.hasPermission('admin:everything')));
  }

  isSales(): Observable<boolean> {
    return this.hasRole('sales');
  }

  private decodeToken(token: string): DecodedToken | null {
    try {
      return JSON.parse(atob(token.split('.')[1])) as DecodedToken;
    } catch (e) {
      return null;
    }
  }
}
